import { _js } from '@ifixit/localize';
import { useContext, useEffect, Fragment, useRef, type JSX } from 'react';
import ReactDOM from 'react-dom';
import { useAsync, IfPending, IfFulfilled, IfRejected, PromiseFn } from 'react-async';
import Modal from '../Generic/modal';
import { KeypressContext } from '../Providers/keypressContext';
import { ApiContext } from '../Providers/apiContext';
import { OrderContext } from '../Providers/orderContext';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import { HotSpotItem } from 'Guide/Schematics/Diagram/Providers/dataContext';
import { THDFetchFunc } from './thdApiModal';
import { TooltipContext } from '../Providers/tooltipContext';
import { BoxProps, Button, Flex, Text, TextProps } from '@chakra-ui/react';
import { orangeTheme } from './colors';
import { Instance } from 'tippy.js';

function Dialog(props: BoxProps) {
   return (
      <Flex
         position="fixed"
         top="50%"
         left="50%"
         transform="translate(-50%, -50%)"
         flexDirection="column"
         alignItems="center"
         justifyContent="center"
         backgroundColor="gray.50"
         boxShadow="2xl"
         borderRadius="md"
         color="gray.800"
         minWidth="300px"
         padding="16px 32px"
         margin="11px 15px"
         {...props}
      />
   );
}

export function MainText(props: TextProps) {
   return (
      <Text
         fontWeight="bold"
         fontSize="16px"
         textAlign="center"
         color="gray.800"
         padding="0px 8px 8px"
         {...props}
      />
   );
}

export function SubText(props: TextProps) {
   return <Text fontSize="14px" textAlign="center" color="gray.800" {...props} />;
}

function ModalContent<T>({
   item,
   promiseFn,
   fetchFn,
   onLoading,
   onError,
   onSuccess,
   onClose,
}: {
   item: HotSpotItem;
   promiseFn: PromiseFn<T>;
   fetchFn: THDFetchFunc;
   onLoading: () => string;
   onError: (_error: Error, _onClose: MinimalDisclosure['onClose']) => JSX.Element;
   onSuccess: (_data: any, _onClose: MinimalDisclosure['onClose']) => JSX.Element;
   onClose: MinimalDisclosure['onClose'];
}) {
   const apiContext = useContext(ApiContext);

   const orderPartArgs = {
      apiKey: apiContext.apiKey,
      apiUrl: apiContext.apiUrl,
      createdUserId: apiContext.createdUserId,
      item,
   };

   const state = useAsync({ promiseFn: promiseFn, fetchFn: fetchFn, ...orderPartArgs });
   return (
      <Fragment>
         {/*
            // @ts-expect-error TS(2322): Type 'AsyncState<T, AbstractState<T>>' is not assignable to type 'AsyncInitial<{}, AbstractState<{}>> | AsyncPending<{}, AbstractState<{}>> | AsyncFulfilled<{}, AbstractState<{}>> | AsyncRejected<...>'. */}
         <IfPending state={state}>
            <MainText>{onLoading()}</MainText>
         </IfPending>
         {/*
            // @ts-expect-error TS(2322): Type 'AsyncState<T, AbstractState<T>>' is not assignable to type 'AsyncInitial<{}, AbstractState<{}>> | AsyncPending<{}, AbstractState<{}>> | AsyncFulfilled<{}, AbstractState<{}>> | AsyncRejected<...>'. */}
         <IfRejected state={state}>{error => onError(error, onClose)}</IfRejected>
         {/*
            // @ts-expect-error TS(2322): Type 'AsyncState<T, AbstractState<T>>' is not assignable to type 'AsyncInitial<{}, AbstractState<{}>> | AsyncPending<{}, AbstractState<{}>> | AsyncFulfilled<{}, AbstractState<{}>> | AsyncRejected<...>'. */}
         <IfFulfilled state={state}>{data => onSuccess(data, onClose)}</IfFulfilled>
      </Fragment>
   );
}

const renderErrorToUser = (error: Error, onClose: MinimalDisclosure['onClose']) => (
   <Fragment>
      <MainText>{_js('Something went wrong:')}</MainText>
      <SubText>{error.toString()}</SubText>
      <OkButton onClose={onClose} />
   </Fragment>
);

const OkButton = ({ onClose }: { onClose: MinimalDisclosure['onClose'] }) => {
   const onOk = () => {
      // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      onClose();
   };

   return (
      <Button
         bgColor={orangeTheme.primaryButton}
         color="white"
         _hover={{
            bgColor: orangeTheme.primaryButtonHover,
         }}
         margin="16px 0px 10px"
         tabIndex={0}
         onClick={onOk}
      >
         {_js('OK')}
      </Button>
   );
};

function LoadingModal<T>({
   fetchFn,
   promiseFn,
   onLoading,
   onError,
   onSuccess,
   isOpen,
   onClose,
}: {
   fetchFn: THDFetchFunc;
   promiseFn: PromiseFn<T>;
   onLoading: () => string;
   onError: (_error: Error, _onClose: MinimalDisclosure['onClose']) => JSX.Element;
   onSuccess: (_data: any, _onClose: MinimalDisclosure['onClose']) => JSX.Element;
} & MinimalDisclosure) {
   const orderContext = useContext(OrderContext);
   const toolTipContext = useContext(TooltipContext);

   const modalRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (!isOpen) {
         return;
      }

      let handlerToUseOnUnmount = toolTipContext.onClickOutsideTooltip;

      const newHandler = (
         instance: Instance<any>,
         event: Event,
         previousHandler: ((instance: Instance<any>, event: Event) => void) | undefined
      ) => {
         if (!(event.target instanceof Element)) {
            return;
         }

         const isClickingInsideModal = modalRef.current?.contains(event.target) || false;
         if (isClickingInsideModal) {
            return;
         }
         previousHandler?.(instance, event);
      };

      toolTipContext.setOnClickOutsideTooltip(
         (previousHandler: ((instance: Instance<any>, event: Event) => void) | undefined) => {
            handlerToUseOnUnmount = previousHandler;
            return (instance: Instance<any>, event: Event) => {
               newHandler(instance, event, previousHandler);
            };
         }
      );

      return () => {
         toolTipContext.setOnClickOutsideTooltip(() => handlerToUseOnUnmount);
      };
      // eslint-disable-next-line react-hooks/refs
   }, [modalRef.current, isOpen]);
   const item = orderContext.item;
   registerShortCuts(isOpen, onClose);

   return ReactDOM.createPortal(
      <Modal isOpen={isOpen} onClose={onClose} ref={modalRef}>
         <Dialog>
            <ModalContent
               item={item}
               onClose={onClose}
               fetchFn={fetchFn}
               promiseFn={promiseFn}
               onLoading={onLoading}
               onError={onError}
               onSuccess={onSuccess}
            />
         </Dialog>
      </Modal>,
      document.body
   );
}

const registerShortCuts = (isOpen: boolean, onClose: MinimalDisclosure['onClose']) => {
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useContext" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   const keypressContext = useContext(KeypressContext);

   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useEffect" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   useEffect(() => {
      const mappings = [{ id: 'orderModal', key: 'Escape', action: onClose }];

      if (isOpen) {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.pushMappings(mappings);
      } else {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.popMappings(mappings);
      }

      return () => {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.popMappings(mappings);
      };
   }, [isOpen]);
};

export { LoadingModal, renderErrorToUser, OkButton };
